import { useEffect, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { MinusSignIcon, PlusSignIcon } from '@hugeicons/core-free-icons';
import { EXTRA_SEAT_MONTHLY_PRICE_USD, MAX_EXTRA_SEATS } from '@overlayqa/shared';
import { PRICING_CARD } from './pricingCard';
import './pricing-cards.css';

export const billingMoney = (amount: number) => '$' + amount.toLocaleString('en-US', { maximumFractionDigits: 2 });
/** Fill a pricing-card string's {placeholders} (./pricingCard.ts). */
export const fillCopy = (template: string, vars: Record<string, string | number>): string =>
  template.replace(/\{(\w+)\}/g, (match, key: string) => (key in vars ? String(vars[key]) : match));

const COPY = PRICING_CARD.copy.stepper;

export interface SubscriptionSeatsProps {
  slug: string;
  name: string;
  included: number;
  members: number;
  total: number;
  base: number;
  period: 'month' | 'annual';
  disabled: boolean;
  onChange: (total: number) => void;
}

/**
 * Total seats on a plan card: the pricing cards template's seat chooser (./pricing-cards.css), the same control the
 * marketing cards carry. The count never goes below the plan's bundle or the people already on the team, nor above
 * the bundle plus MAX_EXTRA_SEATS; a team bigger than that is pointed to sales. The plan + extra-seats breakdown sits
 * here and the total, before tax, sits in the card's price line (TrialExpiredPlanGrid.tsx).
 */
export function SubscriptionSeats({ slug, name, included, members, total, base, disabled, onChange }: SubscriptionSeatsProps) {
  const floor = Math.max(included, members);
  const maximum = included + MAX_EXTRA_SEATS;
  const extras = total - included;
  const extraPrice = extras * EXTRA_SEAT_MONTHLY_PRICE_USD;
  const [input, setInput] = useState(String(total));
  useEffect(() => setInput(String(total)), [total]);
  const commitInput = () => {
    const parsed = Number(input);
    const next = input !== '' && Number.isFinite(parsed) ? Math.max(floor, Math.min(maximum, Math.trunc(parsed))) : total;
    setInput(String(next));
    if (next !== total) onChange(next);
  };
  const help = members > maximum ? COPY.tooBig : members > included ? fillCopy(COPY.minimum, { n: members }) : COPY.choose;

  return <div className="pp-stepper" data-plan={slug}>
    <div className="pp-stepper__row">
      <label htmlFor={`subscribe-seats-${slug}`} className="pp-stepper__label">{COPY.label}</label>
      <div className="pp-stepper__ctl">
        <button type="button" className="pp-step" aria-label={fillCopy(COPY.remove, { name })} disabled={disabled || total <= floor} onClick={() => onChange(total - 1)}>
          <HugeiconsIcon icon={MinusSignIcon} size={16} strokeWidth={2} color="currentColor" className="pp-ic" aria-hidden="true" />
        </button>
        <input id={`subscribe-seats-${slug}`} aria-label={fillCopy(COPY.inputLabel, { name })} type="number" inputMode="numeric" className="pp-step-input" min={floor} max={maximum} value={input} disabled={disabled} aria-describedby={`subscribe-seats-help-${slug}`}
          onChange={event => {
            const value = event.target.value;
            setInput(value);
            const number = Number(value);
            if (value && Number.isInteger(number) && number >= floor && number <= maximum && number !== total) onChange(number);
          }}
          onBlur={commitInput}
          onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); commitInput(); } }}
        />
        <button type="button" className="pp-step" aria-label={fillCopy(COPY.add, { name })} disabled={disabled || total >= maximum} onClick={() => onChange(total + 1)}>
          <HugeiconsIcon icon={PlusSignIcon} size={16} strokeWidth={2} color="currentColor" className="pp-ic" aria-hidden="true" />
        </button>
      </div>
    </div>
    <p id={`subscribe-seats-help-${slug}`} className="pp-stepper__help">{help}</p>
    <p className="pp-stepper__sum" aria-live="polite">
      {extras > 0
        ? fillCopy(COPY.breakdown, { base: billingMoney(base), cost: billingMoney(extraPrice), n: extras, seats: extras === 1 ? 'seat' : 'seats' })
        : COPY.allIncluded}
    </p>
  </div>;
}
